import React from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Chip, Tooltip, Box, CircularProgress, Typography, Skeleton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { AdminUserSummary } from '../../../services/adminUsers';

export interface UsersTableProps {
  users: AdminUserSummary[];
  onEdit: (user: AdminUserSummary) => void;
  loading?: boolean;
}

export const UsersTable: React.FC<UsersTableProps> = ({ users, onEdit, loading = false }) => {
  if (loading) {
    return (
      <Paper className="shadow-card p-4">
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">Loading users…</Typography>
        </Box>
        {[...Array(6)].map((_, i) => (
          <Box key={i} display="grid" gridTemplateColumns="2fr 2fr 1fr 1fr 1fr 1fr 1fr 80px" gap={2} py={1}>
            {[...Array(8)].map((__, j) => (
              <Skeleton key={j} variant="rectangular" height={20} />
            ))}
          </Box>
        ))}
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} className="shadow-card">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Department</TableCell>
            <TableCell>Roles</TableCell>
            <TableCell>Approver</TableCell>
            <TableCell>Scope</TableCell>
            <TableCell>Limit</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Typography variant="body2" color="text.secondary">No users found</Typography>
              </TableCell>
            </TableRow>
          )}
          {users.map((u) => (
            <TableRow key={u.id} hover>
              <TableCell>{u.name}</TableCell>
              <TableCell>{u.email}</TableCell>
              <TableCell>{u.department || '-'}</TableCell>
              <TableCell>
                {Array.isArray(u.roles) && u.roles.length > 0 ? (
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {u.roles.map((r: string) => (
                      <Chip key={`${u.id}-${r}`} size="small" label={r}
                        color={r === 'admin' || r === 'super_admin' ? 'secondary' : (r === 'manager' ? 'primary' : 'default')} />
                    ))}
                  </Box>
                ) : (
                  <Chip size="small" label={u.role} color={u.role === 'admin' || u.role === 'super_admin' ? 'secondary' : 'default'} />
                )}
              </TableCell>
              <TableCell>
                {u.is_approver ? <Chip size="small" color="success" label="Yes" /> : <Chip size="small" color="default" label="No" />}
              </TableCell>
              <TableCell>{u.approval_scope}</TableCell>
              <TableCell>{u.approval_limit ?? '-'}</TableCell>
              <TableCell align="right">
                <Tooltip title="Edit user">
                  <IconButton onClick={() => onEdit(u)} size="small">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UsersTable;
